import { useMemo } from "react";
import { View } from "react-native";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import PhotoDisplayImageContent from "./components/PhotoDisplayImageContent";
import PhotoDisplayLivePhotoContent from "./components/PhotoDisplayLivePhotoContent";
import PhotoDisplayVideoContent from "./components/PhotoDisplayVideoContent";
import { PhotoDisplayContentProps } from "./types";

const PhotoDisplayContent = (props: PhotoDisplayContentProps) => {
	const variant = props.photo.variant;
	switch (variant) {
		case PhotoVariant.LivePhoto:
			return <PhotoDisplayLivePhotoContent {...props} />;
		case PhotoVariant.Video:
			return <PhotoDisplayVideoContent {...props} />;
		case PhotoVariant.Image:
		default:
			// Make sure the default behavior is viable. We don't know what photo variants the server will send
			return <PhotoDisplayImageContent {...props} />;
	}
};

/**
 * Takes a photo (image, video, or live photo) and displays it in its most appropriate form.
 */
const PhotoDisplay = ({
	photo,
	boundingWidth,
	boundingHeight,
	...rest
}: Omit<PhotoDisplayContentProps, "photoWidth" | "photoHeight">) => {
	const { width: photoWidth, height: photoHeight } = useMemo(() => {
		if (!photo.width || !photo.height) {
			return { width: boundingWidth, height: boundingHeight };
		}
		// Contain the photo within the innerWidth and innerHeight
		const aspectRatio = photo.width / photo.height;
		if (aspectRatio > boundingWidth / boundingHeight) {
			return {
				width: boundingWidth,
				height: boundingWidth / aspectRatio,
			};
		}
		return {
			width: boundingHeight * aspectRatio,
			height: boundingHeight,
		};
	}, [boundingHeight, boundingWidth, photo.height, photo.width]);

	return (
		<View
			pointerEvents="box-none"
			style={{
				width: boundingWidth,
				height: boundingHeight,
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<PhotoDisplayContent
				photo={photo}
				boundingWidth={boundingWidth}
				boundingHeight={boundingHeight}
				photoWidth={photoWidth}
				photoHeight={photoHeight}
				{...rest}
			/>
		</View>
	);
};
export default PhotoDisplay;
